import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  DatePicker,
  message,
  Modal,
  Typography,
  Tabs,
  Form,
  Radio,
  Checkbox,
  Timeline,
  Alert,
  Select,
  Tooltip,
  Drawer,
  Empty,
} from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  FieldTimeOutlined,
  RollbackOutlined,
  SearchOutlined,
  DownloadOutlined,
  CheckOutlined,
  CopyOutlined,
  DollarOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';

import axios from '../../utils/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { responsiveFilterSelectProps } from '../../utils/responsiveFilterSelect';
import { formatMoney } from '../../utils/formatMoney';
import { formatDateTime, fromNow } from '../../utils/dateFormat';
import { showApiError } from '../../utils/apiError';
import {
  PageHeader,
  StatCard,
  StatGrid,
  FilterBar,
  TableCard,
  Section,
  StatusTag,
  DRAWER_WIDTH,
  MODAL_WIDTH,
  formModalProps,
  tableDefaults,
  paginationDefaults,
} from '../../components/admin';
import {
  orderStatusLabel,
  orderStatusColor,
  sourceLabel,
  fulfillmentLabel,
  isDirectRechargeOrder,
  orderAttentionFlags,
  orderStatusDescription,
  PURCHASE_STAGE_LABELS,
  SUBMISSION_STATUS_LABELS,
  MAX_PURCHASE_ATTEMPTS,
} from '../../constants/orderDisplay';
import './OrderManagement.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

/** "Needs a person" queues. `value` is the backend `attention` filter and location.state value. */
const ATTENTION_QUEUES = [
  {
    value: 'uncertain',
    label: 'Supplier outcome unknown',
    statKey: 'uncertain',
    tone: 'warning',
    icon: <QuestionCircleOutlined />,
    hint: 'Verify in the supplier portal first',
  },
  {
    value: 'refund_failed',
    label: 'Refund failed',
    statKey: 'refundFailed',
    tone: 'danger',
    icon: <RollbackOutlined />,
    hint: 'Client charged, refund did not go through',
  },
  {
    value: 'reconciling',
    label: 'Reconciling',
    statKey: 'reconciling',
    tone: 'primary',
    icon: <SyncOutlined />,
    hint: 'Manual reconciliation in progress',
  },
  {
    value: 'stuck_pending',
    label: 'Pending > 30 min',
    statKey: 'stuckPending',
    tone: 'warning',
    icon: <FieldTimeOutlined />,
    hint: 'Still waiting on the supplier',
  },
];

/** Status tiles. `key` is the backend `status` filter ('' = every status). */
const STATUS_TILES = [
  { key: '', label: 'All orders', statKey: 'total', tone: 'default', icon: <InboxOutlined /> },
  { key: 'pending', label: orderStatusLabel('pending'), statKey: 'pending', tone: 'warning', icon: <ClockCircleOutlined /> },
  { key: 'completed', label: orderStatusLabel('completed'), statKey: 'completed', tone: 'success', icon: <CheckCircleOutlined /> },
  { key: 'failed', label: orderStatusLabel('failed'), statKey: 'failed', tone: 'danger', icon: <CloseCircleOutlined /> },
];

/** Rows tinted in the table: money is at risk until a person acts. */
const TINTED_FLAGS = ['uncertain', 'refund_failed'];

/** Ant Design colour names used in constants/orderDisplay → StatusTag tones. */
const TONE_BY_COLOR = {
  green: 'success',
  red: 'danger',
  orange: 'warning',
  gold: 'warning',
  processing: 'info',
  blue: 'info',
  purple: 'purple',
};
const toneFor = (color) => TONE_BY_COLOR[color] || 'default';

const renderOrderStatus = (status) => (
  <StatusTag tone={toneFor(orderStatusColor(status))}>{orderStatusLabel(status)}</StatusTag>
);

const renderFlag = (flag) => (
  <Tooltip key={flag.key} title={flag.hint}>
    <span>
      <StatusTag tone={toneFor(flag.color)} dot={false}>
        {flag.label}
      </StatusTag>
    </span>
  </Tooltip>
);

const CODE_STATUS_TONE = {
  available: 'success',
  'sold-not-redeemed': 'warning',
  redeemed: 'info',
  expired: 'danger',
  completed: 'success',
  pending: 'warning',
  failed: 'danger',
};

// Supplier-facing timestamps keep seconds: operators match them against
// supplier portal records.
const fmtDateTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const personName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '-';

/** Persisted when safeRefundOrderDeduction fails (externalOrderData.refund / asyncMonitor). */
function getAutoRefundErrorMessage(order) {
  if (!order) return '';
  const r = order.externalOrderData?.refund;
  if (r?.refundError) return String(r.refundError).trim();
  const am = order.externalOrderData?.asyncMonitor;
  if (am?.lastRefundError) return String(am.lastRefundError).trim();
  return '';
}

/** Failed order with deduction, or auto-refund error recorded — admin can retry once no completed refund tx exists. */
function canShowAdminRefundButton(order) {
  if (!order?.accountDeduction?.deductedAt) return false;
  if (String(order.status) === 'failed') return true;
  return !!getAutoRefundErrorMessage(order);
}

function isUncertainExternalOrder(order) {
  if (!order?.isExternalVoucherOrder || order.status !== 'pending') return false;
  const externalData = order.externalOrderData || {};
  return ['uncertain', 'reconciling'].includes(externalData.providerSubmissionStatus)
    || externalData.purchaseStage === 'uncertain';
}

function hasCompletedRefund(order) {
  if (!order) return false;
  if (order.externalOrderData?.refund?.refundedAt) return true;
  return (order.ledger || []).some((tx) => tx.type === 'refund' && tx.status === 'completed');
}

/** Supplier-side references worth showing, deduplicated, in display order. */
function supplierReferences(order) {
  const data = order?.externalOrderData || {};
  const result = order?.externalOrderData?.providerResult || {};
  const refs = [
    ['Supplier order no', data.orderNo || result.orderNo || null],
    ['Supplier order id', data.order_id || result.order_id || result.orderId || null],
    ['Supplier tr_id', result.tr_id ?? null],
    ['Provider order no', data.internal_provider_order_no || null],
    ['Our reference sent to supplier', data.customerOrderNo || null],
    ['Serial', data.serialNumber || null],
  ];
  const seen = new Set();
  return refs.filter(([, value]) => {
    if (value == null || value === '') return false;
    const key = String(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const OrderManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    source: '',
    attention: '',
    dateRange: null,
  });
  const [stats, setStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [orderCodes, setOrderCodes] = useState({});
  const [codesLoading, setCodesLoading] = useState(false);

  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportSelectedVouchers, setExportSelectedVouchers] = useState([]);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [refundActionLoading, setRefundActionLoading] = useState(false);
  const [reconcileModalVisible, setReconcileModalVisible] = useState(false);
  const [reconcileAction, setReconcileAction] = useState('confirm_fulfilled');
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileForm] = Form.useForm();

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get('/admin/orders/statistics');
      setStats(data || null);
    } catch (error) {
      console.error('Fetch order statistics error:', error);
    }
  }, []);

  const fetchOrders = useCallback(async (page = 1, pageSize = 20, filtersOverride = null) => {
    try {
      setLoading(true);
      const currentFilters = filtersOverride || filters;
      const params = { page, limit: pageSize };
      if (currentFilters.search && currentFilters.search.trim()) params.search = currentFilters.search.trim();
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.source) params.source = currentFilters.source;
      if (currentFilters.attention) params.attention = currentFilters.attention;
      if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
        params.startDate = currentFilters.dateRange[0].startOf('day').toISOString();
        params.endDate = currentFilters.dateRange[1].endOf('day').toISOString();
      }

      const response = await axios.get('/admin/orders', { params });
      const data = response?.data || {};
      const pg = data.pagination || {};
      setOrders(Array.isArray(data.orders) ? data.orders : []);
      setPagination({
        current: page,
        pageSize,
        total: pg.total ?? pg.totalCount ?? pg.totalItems ?? 0,
      });
    } catch (error) {
      console.error('Fetch admin orders error:', error);
      showApiError(error, 'Failed to load orders');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search; every other filter refetches immediately.
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchOrders(1, pagination.pageSize);
    }, filters.search ? 400 : 0);
    return () => clearTimeout(delayedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.source, filters.attention, filters.dateRange]);

  // Pick up navigation state from the Admin Dashboard (status / attention
  // queue / a specific order). Apply once and clear the state so a manual
  // reset does not get re-applied.
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.filterStatus || state.attention || state.source || state.search) {
      setFilters((prev) => ({
        ...prev,
        ...(state.filterStatus ? { status: state.filterStatus } : {}),
        ...(state.attention ? { attention: state.attention } : {}),
        ...(state.source ? { source: state.source } : {}),
        ...(state.search ? { search: String(state.search) } : {}),
      }));
    }
    if (state.orderId) {
      handleViewDetails(state.orderId).catch(() => {
        setFilters((prev) => ({ ...prev, search: state.orderId }));
      });
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate]);

  const handleTableChange = (nextPagination) => {
    fetchOrders(nextPagination.current, nextPagination.pageSize);
  };

  // navigator.clipboard requires a secure context and can throw when the
  // document isn't focused; fall back to the legacy textarea path.
  const safeCopyToClipboard = async (text) => {
    if (text === undefined || text === null) return false;
    const value = String(text);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch (err) {
      console.error('navigator.clipboard.writeText failed:', err);
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      return false;
    }
  };

  const copyWithToast = async (value, label = 'Copied') => {
    const ok = await safeCopyToClipboard(value);
    if (ok) message.success(label);
    else message.error('Could not copy automatically — please copy manually');
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value === undefined ? '' : value }));
  };

  // Tiles work as views: a queue tile shows that whole queue (any status), a
  // status tile shows that status (no queue). Clicking the active queue again
  // goes back to every order.
  const selectQueue = (queue) => {
    setFilters((prev) => ({ ...prev, status: '', attention: prev.attention === queue ? '' : queue }));
  };

  const selectStatus = (status) => {
    setFilters((prev) => ({ ...prev, status, attention: '' }));
  };

  const resetFilters = () => {
    setFilters({ search: '', status: '', source: '', attention: '', dateRange: null });
  };

  const refreshAll = async () => {
    await Promise.all([fetchStats(), fetchOrders(pagination.current, pagination.pageSize)]);
  };

  const handleViewDetails = async (orderId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`/admin/orders/${orderId}`);
      setSelectedOrder(response.data.order);
      setDetailOpen(true);
      await fetchOrderCodes(orderId);
    } catch (error) {
      console.error('Fetch order details error:', error);
      showApiError(error, 'Failed to load order details');
      throw error;
    } finally {
      setDetailLoading(false);
    }
  };

  // UI entry point for opening an order: shows a spinner on the row button
  // and swallows the rethrown error (handleViewDetails already reported it).
  const openOrder = async (orderId) => {
    if (!orderId) return;
    setOpeningId(orderId);
    try {
      await handleViewDetails(orderId);
    } catch {
      // already reported
    } finally {
      setOpeningId(null);
    }
  };

  const closeDetail = () => setDetailOpen(false);

  const handleAdminRefundRetry = () => {
    if (!selectedOrder?._id) return;
    const deduction = selectedOrder.accountDeduction || {};
    const charged = (deduction.fromTopUp || 0) + (deduction.fromCredit || 0);
    const clientName = selectedOrder.clientId?.companyName || 'the client';
    Modal.confirm({
      title: `Refund order ${selectedOrder.orderNumber || ''}?`,
      content: (
        <>
          <strong>{formatMoney(charged, selectedOrder.currency)}</strong> will be returned to {clientName}'s
          top-up balance and/or credit, following the original payment breakdown. If a refund has
          already completed, the server rejects this request.
        </>
      ),
      okText: 'Refund client',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setRefundActionLoading(true);
        try {
          const { data } = await axios.post(`/admin/orders/${selectedOrder._id}/refund`);
          message.success(data.message || 'Refund processed');
          await handleViewDetails(selectedOrder._id);
          await refreshAll();
        } catch (err) {
          showApiError(err, 'Refund failed');
        } finally {
          setRefundActionLoading(false);
        }
      },
    });
  };

  const openUncertainReconciliation = (action) => {
    if (!selectedOrder?._id) return;
    setReconcileAction(action);
    reconcileForm.resetFields();
    reconcileForm.setFieldsValue({
      confirmationOrderNumber: '',
      note: '',
      voucherCodes: '',
      supplierOrderIds: '',
    });
    setReconcileModalVisible(true);
  };

  const submitUncertainReconciliation = async (values) => {
    if (!selectedOrder?._id) return;

    const supplierOrderIds = String(values.supplierOrderIds || '')
      .split(/\r?\n/)
      .map((value) => value.trim())
      .filter(Boolean);
    const cards = reconcileAction === 'confirm_fulfilled'
      ? String(values.voucherCodes || '')
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [code, ...pinParts] = line.split('\t');
            const pin = pinParts.join('\t').trim();
            return { code: code.trim(), ...(pin ? { pin } : {}) };
          })
      : [];

    setReconcileLoading(true);
    try {
      const { data } = await axios.post(
        `/admin/orders/${selectedOrder._id}/reconcile-uncertain`,
        {
          action: reconcileAction,
          confirmationOrderNumber: values.confirmationOrderNumber,
          note: values.note,
          cards,
          supplierOrderIds,
        },
      );
      message.success(data.message || 'Order reconciled');
      setReconcileModalVisible(false);
      reconcileForm.resetFields();
      await handleViewDetails(selectedOrder._id);
      await refreshAll();
    } catch (err) {
      showApiError(err, 'Reconciliation failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  const fetchOrderCodes = async (orderId) => {
    try {
      setCodesLoading(true);
      const response = await axios.get(`/admin/orders/${orderId}/codes`);
      setOrderCodes((prev) => ({ ...prev, [orderId]: response.data.codes || [] }));
    } catch (error) {
      console.error('Fetch order codes error:', error);
      showApiError(error, 'Failed to load voucher codes');
    } finally {
      setCodesLoading(false);
    }
  };

  const codesForItem = (item, idx, allCodes) =>
    allCodes.filter((code) =>
      code.voucherId === item.voucherId ||
      code.voucherName === item.voucherName ||
      code.voucherId?._id === item.voucherId,
    );

  // ── Export codes (per order) ────────────────────────────────────────────
  const openExportModal = () => {
    if (!selectedOrder) return;
    setExportSelectedVouchers((selectedOrder.items || []).map((item, idx) => item.voucherId || String(idx)));
    setExportModalVisible(true);
  };

  const CODE_EXPORT_HEADERS = [
    'Order Number', 'Voucher Name', 'Brand', 'Code', 'Serial Number', 'PIN', 'Status', 'Expiry Date', 'Issued At',
  ];

  const codeRows = (item, itemCodes) => itemCodes.map((code) => [
    selectedOrder?.orderNumber || '',
    item.voucherName || '',
    item.voucherBrand || '',
    code.code || '',
    code.serialNumber || '',
    code.pin || '',
    code.status || '',
    code.expiryDate ? dayjs(code.expiryDate).format('YYYY-MM-DD') : '',
    code.issuedAt || code.assignedAt ? dayjs(code.issuedAt || code.assignedAt).format('YYYY-MM-DD HH:mm:ss') : '',
  ]);

  const exportSelectedVoucherCodes = () => {
    const currentOrderCodes = orderCodes[selectedOrder._id] || [];
    const selectedItems = (selectedOrder.items || []).filter((item, idx) =>
      exportSelectedVouchers.includes(item.voucherId || String(idx)),
    );
    if (exportFormat === 'csv') exportCodesAsCSV(selectedItems, currentOrderCodes);
    else exportCodesAsXLSX(selectedItems, currentOrderCodes);
    setExportModalVisible(false);
  };

  const exportCodesAsCSV = (selectedItems, allCodes) => {
    let exported = 0;
    selectedItems.forEach((item, idx) => {
      const itemCodes = codesForItem(item, idx, allCodes);
      if (itemCodes.length === 0) return;
      const csvContent = [CODE_EXPORT_HEADERS, ...codeRows(item, itemCodes)]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${selectedOrder?.orderNumber}_${(item.voucherName || 'codes').replace(/[^a-z0-9]/gi, '_')}_codes_${dayjs().format('YYYY-MM-DD')}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      exported += 1;
    });
    message.success(`Exported ${exported} voucher type(s) as CSV`);
  };

  const exportCodesAsXLSX = (selectedItems, allCodes) => {
    const workbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    let exported = 0;

    selectedItems.forEach((item, index) => {
      const itemCodes = codesForItem(item, index, allCodes);
      if (itemCodes.length === 0) return;
      const worksheet = XLSX.utils.aoa_to_sheet([CODE_EXPORT_HEADERS, ...codeRows(item, itemCodes)]);
      worksheet['!cols'] = CODE_EXPORT_HEADERS.map((header) => ({ wch: Math.max(header.length, 15) }));

      let baseSheetName = (item.voucherName || `Voucher_${index + 1}`).replace(/[\\/?*[\]:]/g, '_');
      if (baseSheetName.length > 31) baseSheetName = `${baseSheetName.substring(0, 28)}...`;
      let sheetName = baseSheetName;
      let counter = 1;
      while (usedSheetNames.has(sheetName)) {
        const suffix = `_${counter}`;
        const maxLength = 31 - suffix.length;
        sheetName = `${baseSheetName.length > maxLength ? baseSheetName.substring(0, maxLength) : baseSheetName}${suffix}`;
        counter += 1;
      }
      usedSheetNames.add(sheetName);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      exported += 1;
    });

    if (exported === 0) {
      message.warning('No codes to export for the selected voucher types');
      return;
    }
    XLSX.writeFile(workbook, `${selectedOrder?.orderNumber || 'order'}_voucher_codes.xlsx`);
    message.success(`Exported ${exported} voucher type(s) as XLSX`);
  };

  // ── Export the current list page (what the table shows) ────────────────
  const exportCurrentPage = () => {
    if (!orders.length) {
      message.warning('Nothing to export');
      return;
    }
    const headers = [
      'Order Number', 'Status', 'Attention', 'Source', 'Fulfillment', 'Items', 'Quantity',
      'Amount', 'Currency', 'Client', 'Ordered By', 'VIO Reference', 'Attempt', 'Created', 'Failure Reason',
    ];
    const rows = orders.map((o) => [
      o.orderNumber,
      orderStatusLabel(o.status),
      orderAttentionFlags(o).map((f) => f.label).join('; '),
      sourceLabel(o.source),
      fulfillmentLabel(o.fulfillmentType) || '',
      (o.itemsSummary || []).map((i) => `${i.name}${i.brand ? ` (${i.brand})` : ''} x${i.quantity ?? '?'}`).join('; '),
      o.totalQuantity ?? '',
      Number(o.totalAmount ?? 0).toFixed(2),
      o.currency || '',
      o.clientId?.companyName || '',
      o.orderedBy?.email || '',
      o.referenceId || '',
      o.externalPurchaseAttempt || 1,
      fmtDateTime(o.createdAt),
      o.failureReason || '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = headers.map((h) => ({ wch: Math.max(h.length, 16) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
    XLSX.writeFile(workbook, `orders_page${pagination.current}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  // ── Detail drawer sections ──────────────────────────────────────────────

  /**
   * Top of the drawer: why the order is in its state and what the operator
   * can do about it (supplier verification, refund, reconciliation record).
   */
  const renderCallouts = () => {
    const order = selectedOrder;
    const data = order.externalOrderData || {};
    const supplier = sourceLabel(order.source);
    const uncertain = isUncertainExternalOrder(order);
    const reconciling = data.providerSubmissionStatus === 'reconciling';
    const refundError = getAutoRefundErrorMessage(order);
    const refunded = hasCompletedRefund(order);
    const canRefund = canShowAdminRefundButton(order) && !refunded;
    const reconciliation = data.manualReconciliation || {};
    const stuck = orderAttentionFlags(order).find((f) => f.key === 'stuck_pending');
    const deduction = order.accountDeduction || {};
    const charged = (deduction.fromTopUp || 0) + (deduction.fromCredit || 0);
    const blocks = [];

    if (order.status === 'failed') {
      blocks.push(
        <Alert
          key="failed"
          type="error"
          showIcon
          message="Why it failed"
          description={orderStatusDescription(order)}
        />,
      );
    }

    if (order.status === 'pending' && !uncertain) {
      blocks.push(
        <Alert
          key="pending"
          type={stuck ? 'warning' : 'info'}
          showIcon
          message={stuck ? stuck.label : orderStatusDescription(order)}
          description={stuck ? `${orderStatusDescription(order)} ${stuck.hint}` : undefined}
        />,
      );
    }

    if (uncertain) {
      blocks.push(
        <Alert
          key="uncertain"
          type="warning"
          showIcon
          message={
            reconciling
              ? `${supplier} outcome: reconciliation in progress`
              : `${supplier} outcome needs verification`
          }
          description={
            <>
              <div>
                <strong>Do not retry or refund from the timeout alone.</strong>{' '}
                {data.providerSubmissionError ||
                  'The purchase request was sent, but Vouchain did not receive a definitive response.'}
              </div>
              <div className="order-callout__hint">
                Find this purchase in the supplier portal using the order time, product, recharge
                account or member details and amount (see Supplier fulfillment below). Then record
                exactly one verified outcome. Every action is audited.
              </div>
              <Space wrap className="order-callout__actions">
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={reconciling}
                  onClick={() => openUncertainReconciliation('confirm_fulfilled')}
                >
                  Confirm purchased and enter code
                </Button>
                <Button
                  danger
                  icon={<DollarOutlined />}
                  disabled={reconciling}
                  onClick={() => openUncertainReconciliation('confirm_not_purchased')}
                >
                  Confirm not purchased and refund
                </Button>
                {reconciling ? (
                  <Text type="secondary">Unavailable while the reconciliation is running.</Text>
                ) : null}
              </Space>
            </>
          }
        />,
      );
    }

    if (refundError || canRefund) {
      blocks.push(
        <Alert
          key="refund"
          type={refundError && !refunded ? 'error' : refunded ? 'info' : 'warning'}
          showIcon
          message={
            refundError
              ? refunded
                ? 'Automatic refund failed earlier; a refund has since completed'
                : 'Automatic refund failed'
              : 'Client was charged and has not been refunded'
          }
          description={
            <>
              {refundError ? <div>{refundError}</div> : null}
              {canRefund ? (
                <>
                  <div className="order-callout__hint">
                    Refunding returns {formatMoney(charged, order.currency)} to the client using the
                    original top-up / credit breakdown. If a completed refund already exists, the
                    server rejects the request.
                  </div>
                  <div className="order-callout__actions">
                    <Button
                      type="primary"
                      danger
                      icon={<DollarOutlined />}
                      loading={refundActionLoading}
                      onClick={handleAdminRefundRetry}
                    >
                      Refund client now
                    </Button>
                  </div>
                </>
              ) : null}
            </>
          }
        />,
      );
    }

    if (reconciliation.status) {
      blocks.push(
        <Alert
          key="reconciliation"
          type={reconciliation.status === 'completed' ? 'success' : reconciliation.status === 'failed' ? 'error' : 'info'}
          showIcon
          message={`Manual reconciliation: ${reconciliation.action === 'confirm_fulfilled' ? 'confirmed purchased' : 'confirmed not purchased'} (${reconciliation.status})`}
          description={
            <div>
              <div>
                By {reconciliation.reviewedBy || '-'} · started {fmtDateTime(reconciliation.startedAt)} ·
                completed {fmtDateTime(reconciliation.completedAt)}
              </div>
              {reconciliation.note ? <div>Note: {reconciliation.note}</div> : null}
              {reconciliation.supplierOrderIds?.length > 0 ? (
                <div>Supplier order ids: {reconciliation.supplierOrderIds.join(', ')}</div>
              ) : null}
              {reconciliation.error ? <Text type="danger">Error: {reconciliation.error}</Text> : null}
            </div>
          }
        />,
      );
    }

    if (blocks.length === 0) return null;
    return (
      <Section>
        <div className="order-callouts">{blocks}</div>
      </Section>
    );
  };

  const renderSummary = () => {
    const order = selectedOrder;
    const fulfillment = fulfillmentLabel(order.fulfillmentType);
    const attempt = order.externalPurchaseAttempt || 1;
    return (
      <Section>
        <div className="order-facts">
          <div>
            <div className="order-fact__label">Amount</div>
            <div className="order-fact__money">{formatMoney(order.totalAmount, order.currency)}</div>
          </div>
          <div>
            <div className="order-fact__label">Client</div>
            <div className="cell-main">{order.clientId?.companyName || '-'}</div>
            <div className="cell-sub">{personName(order.orderedBy)}</div>
          </div>
          <div>
            <div className="order-fact__label">Supplier</div>
            <div className="cell-main">{sourceLabel(order.source)}</div>
            <div className="cell-sub">
              {[fulfillment, order.isExternalVoucherOrder ? `attempt ${attempt} of ${MAX_PURCHASE_ATTEMPTS}` : null]
                .filter(Boolean)
                .join(' · ') || '-'}
            </div>
          </div>
          <div>
            <div className="order-fact__label">Created</div>
            <div className="cell-main">{formatDateTime(order.createdAt)}</div>
            <div className="cell-sub">{fromNow(order.createdAt)}</div>
          </div>
        </div>
      </Section>
    );
  };

  const renderOrderDetails = () => {
    const order = selectedOrder;
    const failed = order.status === 'failed';
    const retryAllowed = !!order.externalOrderData?.retryAllowed;
    return (
      <Section title="Order details">
        <dl className="kv">
          <dt>Order number</dt>
          <dd>
            <Text copyable className="cell-mono">{order.orderNumber}</Text>
          </dd>
          {order.referenceId ? (
            <>
              <dt>VIO reference</dt>
              <dd>
                <Text copyable className="cell-mono">{order.referenceId}</Text>
                <div className="cell-sub">UserVoucher id. VIO support searches with this.</div>
              </dd>
            </>
          ) : null}
          <dt>Ordered by</dt>
          <dd>
            {[order.orderedBy?.firstName, order.orderedBy?.lastName].filter(Boolean).join(' ') || '-'}
            {order.orderedBy?.email ? <span className="muted"> · {order.orderedBy.email}</span> : null}
          </dd>
          <dt>Created</dt>
          <dd>{fmtDateTime(order.createdAt)}</dd>
          <dt>{failed ? 'Failed at' : 'Processed at'}</dt>
          <dd>{fmtDateTime(failed ? order.timestamps?.failedAt : order.timestamps?.processedAt)}</dd>
          <dt>Attempt</dt>
          <dd>
            <Space size={8} wrap>
              <span>
                {order.externalPurchaseAttempt || 1}
                {order.isExternalVoucherOrder ? ` of ${MAX_PURCHASE_ATTEMPTS}` : ''}
              </span>
              {failed ? (
                <StatusTag tone={retryAllowed ? 'success' : 'default'}>
                  {retryAllowed ? 'Member may retry' : 'No further attempt'}
                </StatusTag>
              ) : null}
            </Space>
          </dd>
          {order.notes ? (
            <>
              <dt>Notes</dt>
              <dd>{order.notes}</dd>
            </>
          ) : null}
        </dl>
      </Section>
    );
  };

  const renderExternalFulfillmentSection = () => {
    if (!selectedOrder?.isExternalVoucherOrder) return null;
    const data = selectedOrder.externalOrderData || {};
    const result = data.providerResult || {};
    const reconciliation = data.manualReconciliation || {};
    const fulfillment = fulfillmentLabel(data.externalFulfillmentType || data.zhongminFulfillmentType);
    const refs = supplierReferences(selectedOrder);
    const providerStatus = result.status || data.fulfillmentSummary?.providerStatus || null;
    const providerCode = result.rc ?? data.error_code ?? data.error_response?.code ?? null;
    const providerMessage = result.message || data.error_message || data.message || data.error_response?.message || null;
    const receipt = result.receipt || result.sn || null;
    const providerTone = /success|completed/i.test(providerStatus || '')
      ? 'success'
      : /fail|error/i.test(providerStatus || '')
        ? 'danger'
        : 'info';

    return (
      <Section title="Supplier fulfillment" description="What Vouchain sent to the supplier and what came back.">
        <div className="admin-grid-2">
          <dl className="kv">
            <dt>Supplier</dt>
            <dd>{sourceLabel(selectedOrder.source)}</dd>
            <dt>Fulfillment</dt>
            <dd>
              {fulfillment || '-'}
              {data.expectedCardsCount ? (
                <span className="muted"> · expected {data.expectedCardsCount} card(s)</span>
              ) : null}
            </dd>
            <dt>Purchase stage</dt>
            <dd>{PURCHASE_STAGE_LABELS[data.purchaseStage] || data.purchaseStage || '-'}</dd>
            <dt>Submission status</dt>
            <dd>{SUBMISSION_STATUS_LABELS[data.providerSubmissionStatus] || data.providerSubmissionStatus || '-'}</dd>
            <dt>Sent to supplier</dt>
            <dd>{fmtDateTime(data.providerSubmittedAt)}</dd>
            <dt>Supplier answered</dt>
            <dd>{fmtDateTime(data.providerResponseReceivedAt)}</dd>
          </dl>
          <dl className="kv">
            <dt>Supplier status</dt>
            <dd>
              <Space size={6} wrap>
                {providerStatus ? <StatusTag tone={providerTone}>{providerStatus}</StatusTag> : '-'}
                {providerCode != null ? <Text code>rc={String(providerCode)}</Text> : null}
              </Space>
            </dd>
            <dt>Supplier message</dt>
            <dd>{providerMessage || '-'}</dd>
            {isDirectRechargeOrder(selectedOrder) ? (
              <>
                <dt>Recharge account</dt>
                <dd>
                  {data.rechargeAccount ? (
                    <Text code copyable>{data.rechargeAccount}</Text>
                  ) : (
                    <Text type="danger">Not recorded</Text>
                  )}
                </dd>
                {receipt ? (
                  <>
                    <dt>Supplier receipt</dt>
                    <dd>
                      <Text code copyable>{String(receipt)}</Text>
                    </dd>
                  </>
                ) : null}
              </>
            ) : null}
            {refs.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>
                  <Text code copyable>{String(value)}</Text>
                </dd>
              </React.Fragment>
            ))}
            {data.providerSubmissionError && data.providerSubmissionError !== reconciliation.note ? (
              <>
                <dt>Submission error</dt>
                <dd>
                  <Text type="danger">{data.providerSubmissionError}</Text>
                  {data.providerHttpStatus ? <span className="muted"> (HTTP {data.providerHttpStatus})</span> : null}
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      </Section>
    );
  };

  const renderAsyncMonitorSection = () => {
    if (!selectedOrder?.isExternalVoucherOrder) return null;
    const monitor = selectedOrder.externalOrderData?.asyncMonitor || {};
    if (!(monitor.firstPendingAt || monitor.lastCheckedAt || monitor.finalizedAt)) return null;
    return (
      <Section title="Async monitor" description="Background status checks with the supplier while the order is pending.">
        <dl className="kv">
          <dt>Pending since</dt>
          <dd>{fmtDateTime(monitor.firstPendingAt)}</dd>
          <dt>Last checked</dt>
          <dd>
            {monitor.lastCheckedAt ? (
              <>
                {fmtDateTime(monitor.lastCheckedAt)} <span className="muted">({fromNow(monitor.lastCheckedAt)})</span>
              </>
            ) : (
              '-'
            )}
          </dd>
          <dt>Last fetch</dt>
          <dd>
            {monitor.lastFetchStatus || '-'}
            {monitor.lastFetchError ? <Text type="danger"> — {monitor.lastFetchError}</Text> : null}
          </dd>
          <dt>Finalized</dt>
          <dd>{monitor.finalizedAt ? `${fmtDateTime(monitor.finalizedAt)} · ${monitor.finalizationReason || ''}` : '-'}</dd>
          {monitor.consecutiveNotFoundCount > 0 ? (
            <>
              <dt>Supplier lookups that found nothing</dt>
              <dd>{monitor.consecutiveNotFoundCount}</dd>
            </>
          ) : null}
        </dl>
      </Section>
    );
  };

  const renderMoneySection = () => {
    if (!selectedOrder) return null;
    const deduction = selectedOrder.accountDeduction || {};
    const ledger = Array.isArray(selectedOrder.ledger) ? selectedOrder.ledger : [];
    const refund = selectedOrder.externalOrderData?.refund || {};
    const refundError = getAutoRefundErrorMessage(selectedOrder);
    const refunded = hasCompletedRefund(selectedOrder);
    const currency = selectedOrder.currency;

    return (
      <Section title="Client wallet & ledger">
        <div className="admin-grid-2">
          <dl className="kv">
            <dt>Charged to client</dt>
            <dd>
              {deduction.deductedAt ? (
                <>
                  <span className="cell-money">
                    {formatMoney((deduction.fromTopUp || 0) + (deduction.fromCredit || 0), currency)}
                  </span>
                  <div className="cell-sub">
                    Top-up {formatMoney(deduction.fromTopUp || 0, currency)} · credit{' '}
                    {formatMoney(deduction.fromCredit || 0, currency)}
                  </div>
                </>
              ) : (
                <span className="muted">Not charged</span>
              )}
            </dd>
            <dt>Charged at</dt>
            <dd>{fmtDateTime(deduction.deductedAt)}</dd>
          </dl>
          <dl className="kv">
            <dt>Refund</dt>
            <dd>
              {refunded ? (
                <StatusTag tone="purple">
                  Refunded{refund.amount != null ? ` ${formatMoney(refund.amount, currency)}` : ''}
                </StatusTag>
              ) : refundError ? (
                <StatusTag tone="danger">Refund failed</StatusTag>
              ) : selectedOrder.status === 'failed' && deduction.deductedAt ? (
                <StatusTag tone="warning">Not refunded</StatusTag>
              ) : (
                '-'
              )}
              {refund.refundedAt ? <div className="cell-sub">{fmtDateTime(refund.refundedAt)}</div> : null}
            </dd>
            <dt>Order total</dt>
            <dd>
              <span className="cell-money">{formatMoney(selectedOrder.totalAmount, currency)}</span>
            </dd>
            <dt>Payment mode</dt>
            <dd>{selectedOrder.paymentMode || 'auto'}</dd>
          </dl>
        </div>

        {ledger.length > 0 ? (
          <Table
            className="order-subtable"
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowKey={(tx) => tx._id}
            dataSource={ledger}
            columns={[
              {
                title: 'Type',
                dataIndex: 'type',
                width: 100,
                render: (t) => (
                  <StatusTag tone={t === 'refund' ? 'purple' : t === 'consume' ? 'info' : 'default'}>{t}</StatusTag>
                ),
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                width: 140,
                align: 'right',
                render: (a) => <span className="cell-money">{formatMoney(a, currency)}</span>,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 110,
                render: (s) => (
                  <StatusTag tone={s === 'completed' ? 'success' : s === 'cancelled' ? 'default' : 'warning'}>{s}</StatusTag>
                ),
              },
              { title: 'Reference', dataIndex: 'reference', render: (r) => (r ? <span className="cell-mono">{r}</span> : '-') },
              { title: 'Note', key: 'note', render: (_, tx) => tx.notes || tx.description || '-' },
              { title: 'Time', dataIndex: 'createdAt', width: 170, render: fmtDateTime },
            ]}
          />
        ) : null}
      </Section>
    );
  };

  const renderAttemptsSection = () => {
    const siblings = Array.isArray(selectedOrder?.siblingAttempts) ? selectedOrder.siblingAttempts : [];
    if (siblings.length === 0) return null;
    return (
      <Section
        title="Other attempts for the same member voucher"
        description="Earlier or later supplier purchases made for the same VIO member voucher."
      >
        <Table
          className="order-subtable"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowKey={(row) => row.orderId}
          dataSource={siblings}
          columns={[
            { title: 'Attempt', dataIndex: 'purchaseAttempt', width: 80 },
            {
              title: 'Order',
              dataIndex: 'orderNumber',
              render: (n, row) => (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  loading={openingId === row.orderId}
                  onClick={() => openOrder(row.orderId)}
                >
                  {n}
                </Button>
              ),
            },
            { title: 'Status', dataIndex: 'status', width: 120, render: renderOrderStatus },
            {
              title: 'Amount',
              key: 'amount',
              width: 140,
              align: 'right',
              render: (_, row) => <span className="cell-money">{formatMoney(row.totalAmount, row.currency)}</span>,
            },
            { title: 'Created', dataIndex: 'createdAt', width: 170, render: fmtDateTime },
            { title: 'Failure reason', dataIndex: 'failureReason', render: (r) => r || '-' },
          ]}
        />
      </Section>
    );
  };

  const renderProviderCallbackSection = () => {
    if (!selectedOrder?.isExternalVoucherOrder) return null;
    const externalData = selectedOrder.externalOrderData || {};

    const callbackHistory = Array.isArray(externalData.callbackHistory) ? externalData.callbackHistory : [];
    const providerCallbackHistory = Array.isArray(externalData.providerCallbackHistory)
      ? externalData.providerCallbackHistory
      : Array.isArray(externalData.providerResult?.providerCallbackHistory)
        ? externalData.providerResult.providerCallbackHistory
        : [];

    if (callbackHistory.length === 0 && providerCallbackHistory.length === 0) return null;

    const mainTimelineItems = callbackHistory.map((entry) => {
      const color = entry.action?.includes('completed') ? 'green'
        : entry.action?.includes('failed') ? 'red'
        : entry.action?.includes('ignored') ? 'gray'
        : 'blue';
      return {
        color,
        children: (
          <div>
            <div>
              <span className="cell-main">Attempt #{entry.attempt}</span>{' '}
              <span className="muted">{fmtDateTime(entry.receivedAt)}</span>
            </div>
            <div className="cell-sub">
              Status {entry.status || 'unknown'} · {entry.cardsCount || 0} card(s) · {entry.action || 'received'}
            </div>
            {entry.failReason ? <Text type="danger">{entry.failReason}</Text> : null}
          </div>
        ),
      };
    });

    const providerTimelineItems = providerCallbackHistory.map((entry, idx) => {
      const action = entry.action || '';
      const color = action.includes('SUCCESS') || action.includes('PROCESSED') ? 'green'
        : action.includes('FAIL') || action.includes('ERROR') ? 'red'
        : action.includes('SIGN') || action.includes('IDEMPOTENT') ? 'gray'
        : 'blue';
      return {
        color,
        children: (
          <div>
            <div>
              <span className="cell-main">#{idx + 1}</span>{' '}
              <span className="muted">{fmtDateTime(entry.loggedAt || entry.receivedAt)}</span>
            </div>
            <div className="cell-sub">
              {action || 'callback'}
              {entry.orderNo ? (
                <>
                  {' · '}
                  <Text code copyable style={{ fontSize: 12 }}>{String(entry.orderNo)}</Text>
                </>
              ) : null}
            </div>
            {entry.detail != null ? (
              <pre className="order-payload">
                {typeof entry.detail === 'string' ? entry.detail : JSON.stringify(entry.detail, null, 2)}
              </pre>
            ) : null}
          </div>
        ),
      };
    });

    return (
      <Section
        title="Supplier callbacks"
        description={`Received by provider-service: ${externalData.providerCallbackAttempts ?? providerCallbackHistory.length} · Applied by Vouchain: ${externalData.callbackAttempts || callbackHistory.length}`}
      >
        <div className="admin-grid-2">
          {providerCallbackHistory.length > 0 ? (
            <div>
              <div className="order-subhead">Supplier → provider-service</div>
              <Timeline items={providerTimelineItems} />
            </div>
          ) : null}
          {callbackHistory.length > 0 ? (
            <div>
              <div className="order-subhead">provider-service → Vouchain</div>
              <Timeline items={mainTimelineItems} />
            </div>
          ) : null}
        </div>
      </Section>
    );
  };

  const renderItemBlock = (item, idx, allCodes) => {
    const itemCodes = codesForItem(item, idx, allCodes);
    const direct = isDirectRechargeOrder(selectedOrder);
    const currency = selectedOrder.currency;
    const brand = item.voucherDetails?.brand || item.voucherBrand;

    return (
      <div className="order-item">
        <div className="order-facts">
          <div>
            <div className="order-fact__label">Quantity ordered</div>
            <div className="cell-main">{item.quantity ?? '-'}</div>
          </div>
          <div>
            <div className="order-fact__label">Unit price</div>
            <div className="cell-main cell-money">{formatMoney(item.unitPrice, currency)}</div>
          </div>
          <div>
            <div className="order-fact__label">Line total</div>
            <div className="cell-main cell-money">{formatMoney(item.totalPrice, currency)}</div>
          </div>
          <div>
            <div className="order-fact__label">{direct ? 'Codes' : 'Codes delivered'}</div>
            <div className="cell-main">
              {direct ? 'None (direct top-up)' : `${itemCodes.length} / ${item.quantity ?? '?'}`}
            </div>
          </div>
        </div>
        {brand || item.voucherId ? (
          <div className="cell-sub order-item__meta">
            {brand ? `Brand ${brand}` : null}
            {brand && item.voucherId ? ' · ' : null}
            {item.voucherId ? (
              <>
                Voucher id <span className="cell-mono">{String(item.voucherId)}</span>
              </>
            ) : null}
          </div>
        ) : (
          <div className="order-item__meta" />
        )}

        {itemCodes.length > 0 ? (
          <Table
            className="order-subtable"
            dataSource={itemCodes}
            loading={codesLoading}
            rowKey={(record, index) => record._id || record.code || record.externalOrderId || `code-${index}`}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 220, render: (code) => <Text copyable className="cell-mono">{code}</Text> },
              { title: 'Serial', dataIndex: 'serialNumber', width: 150, render: (s) => (s ? <span className="cell-mono">{s}</span> : '-') },
              { title: 'PIN', dataIndex: 'pin', width: 120, render: (pin) => (pin ? <Text code copyable>{pin}</Text> : '-') },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 150,
                render: (status) => <StatusTag tone={CODE_STATUS_TONE[status] || 'default'}>{status || '-'}</StatusTag>,
              },
              { title: 'Expiry', dataIndex: 'expiryDate', width: 120, render: (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '-') },
              { title: 'Issued', key: 'issued', width: 160, render: (_, c) => formatDateTime(c.assignedAt || c.usedAt || c.createdAt) },
            ]}
          />
        ) : (
          <div className="order-empty-codes">
            {codesLoading ? (
              <Text type="secondary">Loading codes…</Text>
            ) : direct ? (
              <Text type="secondary">
                Direct top-up: no code is issued. The value is credited to the recharge account in Supplier fulfillment.
              </Text>
            ) : selectedOrder.status === 'pending' ? (
              <Text type="secondary">No code yet. The supplier has not fulfilled this order.</Text>
            ) : selectedOrder.status === 'failed' ? (
              <Text type="secondary">No code. The order failed.</Text>
            ) : (
              <Text type="warning">The order is completed but no code is recorded. Check Supplier fulfillment above.</Text>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderItemsSection = () => {
    const items = selectedOrder?.items || [];
    const currentOrderCodes = orderCodes[selectedOrder._id] || [];
    const hasCodes = items.some((item, idx) => codesForItem(item, idx, currentOrderCodes).length > 0);

    let body;
    if (items.length === 0) {
      body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items on this order" />;
    } else if (items.length === 1) {
      body = renderItemBlock(items[0], 0, currentOrderCodes);
    } else {
      body = (
        <Tabs
          key={selectedOrder._id}
          defaultActiveKey={items[0]?.voucherId || '0'}
          items={items.map((item, idx) => {
            const itemCodes = codesForItem(item, idx, currentOrderCodes);
            const name = item.voucherDetails?.name || item.voucherName || 'Unknown voucher';
            return {
              key: item.voucherId || String(idx),
              label: `${name} (${item.quantity ?? itemCodes.length})`,
              children: renderItemBlock(item, idx, currentOrderCodes),
            };
          })}
        />
      );
    }

    return (
      <Section
        title={items.length === 1 ? 'Item & voucher codes' : 'Items & voucher codes'}
        description={
          items.length === 1
            ? items[0].voucherDetails?.name || items[0].voucherName || 'Unknown voucher'
            : undefined
        }
        extra={
          hasCodes ? (
            <Button size="small" icon={<DownloadOutlined />} onClick={openExportModal}>
              Export codes
            </Button>
          ) : null
        }
      >
        {body}
      </Section>
    );
  };

  // ── List ────────────────────────────────────────────────────────────────

  const needsPerson = (record) => orderAttentionFlags(record).some((f) => TINTED_FLAGS.includes(f.key));

  const columns = [
    {
      title: 'Order',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 230,
      render: (orderNumber, record) => (
        <div>
          <div className="orders-cell-head">
            <span className="cell-main cell-mono">{orderNumber}</span>
            <Tooltip title="Copy order number">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                aria-label={`Copy order number ${orderNumber}`}
                onClick={(e) => {
                  e.stopPropagation();
                  copyWithToast(orderNumber, 'Order number copied');
                }}
              />
            </Tooltip>
          </div>
          <div className="cell-sub">
            {fromNow(record.createdAt)}
            {(record.externalPurchaseAttempt || 1) > 1 ? ` · attempt ${record.externalPurchaseAttempt}` : ''}
            {record.referenceId ? (
              <>
                {' · '}
                <Tooltip title="VIO member voucher reference (UserVoucher id). VIO support quotes this id. Click to copy.">
                  <button
                    type="button"
                    className="orders-ref"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyWithToast(record.referenceId, 'VIO reference copied');
                    }}
                  >
                    VIO …{String(record.referenceId).slice(-8)} <CopyOutlined />
                  </button>
                </Tooltip>
              </>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: 'Client & item',
      key: 'client',
      width: 260,
      render: (_, record) => {
        const items = record.itemsSummary || [];
        const [first, ...rest] = items;
        return (
          <div>
            <div className="cell-main">
              {record.clientId?.companyName || <span className="muted">Unknown client</span>}
            </div>
            <div className="cell-sub">
              {first ? `${first.name}${first.brand ? ` · ${first.brand}` : ''} × ${first.quantity ?? '?'}` : 'No items'}
              {rest.length > 0 ? (
                <Tooltip title={rest.map((i) => `${i.name} × ${i.quantity ?? '?'}`).join(', ')}>
                  <span> · +{rest.length} more</span>
                </Tooltip>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Supplier',
      key: 'source',
      width: 170,
      render: (_, record) => {
        const fulfillment = fulfillmentLabel(record.fulfillmentType);
        const account = record.externalOrderData?.rechargeAccount;
        return (
          <div>
            <div>{sourceLabel(record.source).replace(' (internal)', '')}</div>
            {fulfillment || account ? (
              <div className="cell-sub">
                {fulfillment}
                {fulfillment && account ? ' · ' : null}
                {account ? <span className="cell-mono">{account}</span> : null}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (status, record) => {
        const flags = orderAttentionFlags(record);
        return (
          <div>
            {renderOrderStatus(status)}
            {flags.length > 0 ? <div className="orders-flags">{flags.map(renderFlag)}</div> : null}
            {status === 'failed' && record.failureReason ? (
              <Tooltip title={record.failureReason}>
                <div className="orders-reason">{record.failureReason}</div>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      align: 'right',
      render: (amount, record) => (
        <div>
          <div className="cell-money">{formatMoney(amount, record.currency)}</div>
          {record.refunded ? <div className="cell-sub">Refunded</div> : null}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => <span style={{ whiteSpace: 'nowrap' }}>{formatDateTime(date)}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, record) => {
        const urgent = needsPerson(record);
        return (
          <Button
            type={urgent ? 'primary' : 'default'}
            size="small"
            loading={openingId === record._id}
            aria-label={`${urgent ? 'Review' : 'View'} order ${record.orderNumber}`}
            onClick={(e) => {
              e.stopPropagation();
              openOrder(record._id);
            }}
          >
            {urgent ? 'Review' : 'View'}
          </Button>
        );
      },
    },
  ];

  const sourceOptions = [
    { value: 'vouchain', label: sourceLabel('vouchain') },
    ...Object.keys(stats?.sources || {})
      .filter((key) => key !== 'vouchain')
      .sort()
      .map((key) => ({ value: key, label: `${sourceLabel(key)} (${stats.sources[key].total})` })),
  ];

  const attentionTotal = stats
    ? ATTENTION_QUEUES.reduce((sum, q) => sum + (stats.attention?.[q.statKey] || 0), 0)
    : 0;

  const activeQueue = ATTENTION_QUEUES.find((q) => q.value === filters.attention);
  const filtersActive = !!(
    filters.search ||
    filters.status ||
    filters.source ||
    filters.attention ||
    (filters.dateRange && filters.dateRange.length)
  );
  const tableTitle =
    [activeQueue?.label, filters.status ? orderStatusLabel(filters.status) : null].filter(Boolean).join(' · ') ||
    'All orders';
  const emptyText = activeQueue
    ? 'Nothing in this queue'
    : filtersActive
      ? 'No orders match these filters'
      : 'No orders yet';

  const summaryFlags = selectedOrder ? orderAttentionFlags(selectedOrder) : [];
  const reconcileFulfilled = reconcileAction === 'confirm_fulfilled';
  const reconcileDeduction = selectedOrder?.accountDeduction || {};

  return (
    <div className="orders-page">
      <PageHeader
        title="Orders"
        subtitle="Every voucher order, from Vouchain stock or a supplier. Clear the queues that need a person first."
        extra={
          <>
            <Button icon={<ExportOutlined />} onClick={exportCurrentPage} disabled={!orders.length}>
              Export this page
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>
              Refresh
            </Button>
          </>
        }
      />

      <div className="orders-group">
        <span className="orders-group__title">Needs a person</span>
        <span className={clsx('orders-group__note', { 'is-hot': attentionTotal > 0 })}>
          {!stats ? '' : attentionTotal === 0 ? 'Nothing waiting' : `${attentionTotal} order${attentionTotal === 1 ? '' : 's'} waiting`}
        </span>
      </div>
      <StatGrid min={200}>
        {ATTENTION_QUEUES.map((q) => {
          const count = stats?.attention?.[q.statKey] || 0;
          return (
            <StatCard
              key={q.value}
              label={q.label}
              value={stats ? count : '-'}
              icon={q.icon}
              tone={count > 0 ? q.tone : 'default'}
              hint={q.hint}
              highlight={count > 0}
              active={filters.attention === q.value}
              onClick={() => selectQueue(q.value)}
            />
          );
        })}
      </StatGrid>

      <div className="orders-group">
        <span className="orders-group__title">By status</span>
      </div>
      <StatGrid min={200}>
        {STATUS_TILES.map((tile) => (
          <StatCard
            key={tile.key || 'all'}
            label={tile.label}
            value={stats?.[tile.statKey] ?? '-'}
            icon={tile.icon}
            tone={tile.tone}
            hint={stats?.last24h ? `${stats.last24h[tile.statKey] ?? 0} in the last 24h` : undefined}
            active={!filters.attention && filters.status === tile.key}
            onClick={() => selectStatus(tile.key)}
          />
        ))}
      </StatGrid>

      <FilterBar onReset={resetFilters} resetDisabled={!filtersActive}>
        <FilterBar.Item grow>
          <Input
            placeholder="Search order no, VIO reference, supplier order no, recharge account, client or email"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            allowClear
          />
        </FilterBar.Item>
        <FilterBar.Item width={150}>
          <Select
            placeholder="All statuses"
            value={filters.status || undefined}
            onChange={(value) => handleFilterChange('status', value || '')}
            allowClear
            {...responsiveFilterSelectProps}
            options={[
              { value: 'pending', label: orderStatusLabel('pending') },
              { value: 'completed', label: orderStatusLabel('completed') },
              { value: 'failed', label: orderStatusLabel('failed') },
            ]}
          />
        </FilterBar.Item>
        <FilterBar.Item width={190}>
          <Select
            placeholder="All suppliers"
            value={filters.source || undefined}
            onChange={(value) => handleFilterChange('source', value || '')}
            allowClear
            {...responsiveFilterSelectProps}
            options={sourceOptions}
          />
        </FilterBar.Item>
        <FilterBar.Item width={210}>
          <Select
            placeholder="Any queue"
            value={filters.attention || undefined}
            onChange={(value) => handleFilterChange('attention', value || '')}
            allowClear
            {...responsiveFilterSelectProps}
            options={ATTENTION_QUEUES.map((q) => ({ value: q.value, label: q.label }))}
          />
        </FilterBar.Item>
        <FilterBar.Item width={250}>
          <RangePicker value={filters.dateRange} onChange={(dates) => handleFilterChange('dateRange', dates)} />
        </FilterBar.Item>
      </FilterBar>

      <TableCard title={tableTitle} count={pagination.total}>
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={orders}
          rowKey={(record) => record?._id || record?.id || record?.orderNumber}
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} /> }}
          pagination={{
            ...paginationDefaults,
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
          rowClassName={(record) => clsx('row-clickable', { 'order-row-attention': needsPerson(record) })}
          onRow={(record) => ({
            onClick: () => {
              // Let operators select text in a row without opening the drawer.
              if (window.getSelection?.()?.toString()) return;
              openOrder(record._id);
            },
          })}
        />
      </TableCard>

      {/* Order detail */}
      <Drawer
        rootClassName="order-drawer"
        title={
          selectedOrder ? (
            <Space size={8} wrap>
              <span>Order {selectedOrder.orderNumber}</span>
              {renderOrderStatus(selectedOrder.status)}
              {summaryFlags.map(renderFlag)}
            </Space>
          ) : (
            'Order'
          )
        }
        open={detailOpen}
        onClose={closeDetail}
        afterOpenChange={(open) => {
          if (!open) setSelectedOrder(null);
        }}
        width={DRAWER_WIDTH.xl}
        destroyOnHidden
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={detailLoading}
            onClick={() => selectedOrder && openOrder(selectedOrder._id)}
          >
            Refresh
          </Button>
        }
      >
        {selectedOrder ? (
          <>
            {renderCallouts()}
            {renderSummary()}
            {renderOrderDetails()}
            {renderExternalFulfillmentSection()}
            {renderAsyncMonitorSection()}
            {renderMoneySection()}
            {renderItemsSection()}
            {renderAttemptsSection()}
            {renderProviderCallbackSection()}
          </>
        ) : null}
      </Drawer>

      <Modal
        title={reconcileFulfilled ? 'Confirm purchased and record codes' : 'Confirm not purchased and refund'}
        open={reconcileModalVisible}
        onCancel={() => { if (!reconcileLoading) setReconcileModalVisible(false); }}
        width={MODAL_WIDTH.md}
        {...formModalProps}
        footer={[
          <Button key="cancel" onClick={() => setReconcileModalVisible(false)} disabled={reconcileLoading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={!reconcileFulfilled}
            loading={reconcileLoading}
            onClick={() => reconcileForm.submit()}
          >
            {reconcileFulfilled ? 'Record as fulfilled' : 'Mark failed and refund client'}
          </Button>,
        ]}
      >
        <p style={{ marginTop: 0 }}>
          {reconcileFulfilled ? (
            <>
              Order <strong>{selectedOrder?.orderNumber}</strong> will be recorded as purchased from{' '}
              {sourceLabel(selectedOrder?.source)}. Any code you enter is delivered to VIO on its next status check.
            </>
          ) : (
            <>
              Order <strong>{selectedOrder?.orderNumber}</strong> will be failed permanently and the original
              Vouchain wallet deduction
              {reconcileDeduction.deductedAt ? (
                <>
                  {' '}(
                  <strong>
                    {formatMoney(
                      (reconcileDeduction.fromTopUp || 0) + (reconcileDeduction.fromCredit || 0),
                      selectedOrder?.currency,
                    )}
                  </strong>
                  )
                </>
              ) : null}{' '}
              returned to {selectedOrder?.clientId?.companyName || 'the client'}.
            </>
          )}
        </p>
        <Alert
          type={reconcileFulfilled ? 'info' : 'warning'}
          showIcon
          message="Supplier-side verification is required"
          description={
            reconcileFulfilled
              ? 'Only continue after locating the successful purchase in the supplier portal.'
              : 'Only continue after confirming in the supplier portal that no purchase was created.'
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={reconcileForm} layout="vertical" onFinish={submitUncertainReconciliation}>
          <Form.Item
            label="Type the Vouchain order number to confirm"
            name="confirmationOrderNumber"
            rules={[
              { required: true, message: 'Order number is required' },
              {
                validator: (_, value) =>
                  value === selectedOrder?.orderNumber
                    ? Promise.resolve()
                    : Promise.reject(new Error('Order number does not match')),
              },
            ]}
          >
            <Input placeholder={selectedOrder?.orderNumber || 'Order number'} />
          </Form.Item>

          {reconcileAction === 'confirm_fulfilled' && !isDirectRechargeOrder(selectedOrder) && (
            <Form.Item
              label={`Voucher codes: ${Math.max(1, Number(selectedOrder?.externalOrderData?.expectedCardsCount) || Number(selectedOrder?.items?.[0]?.quantity) || 1)} required, one per line (add a PIN after a tab)`}
              name="voucherCodes"
              rules={[{ required: true, message: 'Voucher code is required' }]}
            >
              <Input.TextArea rows={5} placeholder={'CODE-1\nCODE-2\tOPTIONAL-PIN'} />
            </Form.Item>
          )}

          <Form.Item label="Supplier order ID(s), if available (one per line)" name="supplierOrderIds">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            label="Verification note"
            name="note"
            rules={[
              { required: true, message: 'Describe how you verified the supplier outcome' },
              { min: 10, message: 'Use at least 10 characters' },
              { max: 1000, message: 'Use at most 1000 characters' },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea rows={4} placeholder="What was checked in the supplier portal, by whom, and what outcome was found?" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Export voucher codes"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        width={MODAL_WIDTH.md}
        footer={[
          <Button key="cancel" onClick={() => setExportModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            disabled={exportSelectedVouchers.length === 0}
            onClick={exportSelectedVoucherCodes}
          >
            Download codes
          </Button>,
        ]}
      >
        <p style={{ marginTop: 0 }}>
          Download the codes issued on order <strong>{selectedOrder?.orderNumber}</strong>. The file includes
          PINs, so store it securely.
        </p>
        <Form layout="vertical">
          <Form.Item label="Voucher types">
            <Checkbox.Group value={exportSelectedVouchers} onChange={setExportSelectedVouchers}>
              <Space direction="vertical" size={6}>
                {selectedOrder?.items?.map((item, idx) => {
                  const itemCodes = codesForItem(item, idx, orderCodes[selectedOrder._id] || []);
                  const voucherKey = item.voucherId || String(idx);
                  return (
                    <Checkbox value={voucherKey} key={voucherKey}>
                      {item.voucherDetails?.name || item.voucherName || 'Unknown voucher'}{' '}
                      <span className="muted">
                        ({itemCodes.length} code{itemCodes.length === 1 ? '' : 's'})
                      </span>
                    </Checkbox>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            label="Format"
            extra={
              exportFormat === 'csv'
                ? 'One CSV file per voucher type.'
                : 'One workbook with a sheet per voucher type.'
            }
            style={{ marginBottom: 0 }}
          >
            <Radio.Group value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} optionType="button">
              <Radio.Button value="xlsx">Excel (.xlsx)</Radio.Button>
              <Radio.Button value="csv">CSV (.csv)</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderManagement;
